/**
 * autocomplete.js
 */

import React from 'react';
import { TextField, Portal } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import get from 'lodash.get';


const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    pointerEvents: 'none',
    [theme.breakpoints.down('sm')]: {
      backgroundColor: theme.palette.comp.popover.underBackground
    }
  }
}));


export default function Autocomplete(props) {
  const {
    formContext: formContextProp,
    name,
    label,
    rules,
    options,
    defaultValue,
    renderOption,
    getOptionLabel,
    isOptionEqualToValue,
    filterOptions,
    onChange,
    onInputChange,
    setValueAs,
    makeValue,
    focusHandlers,
    clearOnBlur,
    classes,
    ...rest
  } = props;

  const styles = useStyles();
  const formContextDefault = useFormContext();
  const formContext = formContextProp || formContextDefault;
  const error = get(formContext.formState.errors, name);
  const customValue = React.useRef('');
  const [isOpen, setIsOpen] = React.useState(false);


  return (
    <>
      {/* full-screen div under open popper allows emphasis-styling */}
      {isOpen &&
      <Portal>
        <div className={styles.root}></div>
      </Portal>}

      <Controller
        control={formContext.control}
        name={name}
        rules={rules}
        defaultValue={defaultValue}
        render={({ field: { ref, value, onChange, ...inputProps } }) => {
          return <>
            <MuiAutocomplete
              freeSolo
              selectOnFocus
              blurOnSelect
              clearOnBlur
              classes={classes}
              value={value}
              options={options}
              renderOption={renderOption}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue}
              filterOptions={filterOptions}

              // set open state for root portal div
              onOpen={(e) => {
                setIsOpen(true);
              }}
              onClose={(e, r) => {
                setIsOpen(false);
              }}

              onChange={(e, val, reason) => {
                // clear custom value when option has been selected
                customValue.current = '';
                let newVal = val;

                if (reason === 'clear') {
                  newVal = makeValue ? makeValue('') : '';
                } else if (reason === 'create-option') {
                  newVal = makeValue ? makeValue(val) : val;
                }

                onChange(setValueAs ? setValueAs(newVal) : newVal);
                if (props.onChange) props.onChange(e, val, reason);
              }}
              onInputChange={(e, val, r) => {
                if (r === 'input') {
                  // update custom value text
                  customValue.current = val;
                  if (onInputChange) {
                    onInputChange(val);
                  }
                }
              }}
              onBlur={e => {
                if (customValue.current !== '' && !clearOnBlur) {
                  // set value using custom text
                  const val = makeValue ? makeValue(customValue.current) : customValue.current;
                  onChange(setValueAs ? setValueAs(val) : val);
                  if (props.onChange) props.onChange(e, val, 'blur');
                }
              }}
              renderInput={params => {
                params.inputProps['data-testid'] = `${name}-input`;
                return (
                  <TextField
                    {...params}
                    {...inputProps}  
                    inputRef={ref}
                    label={label}
                    variant="outlined"
                    size="small"
                    error={Boolean(error)}
                    helperText={error?.message}
                    onFocus={e => {
                      if (focusHandlers) {
                        focusHandlers.onFocus(e);
                      }
                    }}
                    onBlur={e => {
                      inputProps.onBlur(e);
                      if (focusHandlers) {
                        focusHandlers.onBlur(e);
                      }
                    }}
                    {...rest}
                  />
                );
              }}
            />
          </>;
        }}
      />
    </>
  );
}